<template>
  <aside :class="['sidebar', { collapsed }]">
    <button class="collapse-btn-vertical" @click="collapsed = !collapsed" :title="collapsed ? 'Expand' : 'Collapse'">
      <span v-if="collapsed">»</span>
      <span v-else>«</span>
    </button>
    <div class="sidebar-top">
      <div class="sidebar-logo" :class="{ 'sidebar-logo-collapsed': collapsed }">
        <img v-if="theme === 'dark'" :src="AdminDark" alt="Seez Admin Logo Dark" class="sidebar-logo-img" />
        <img v-else :src="AdminLight" alt="Seez Admin Logo Light" class="sidebar-logo-img" />
      </div>
    </div>
    <nav class="sidebar-nav">
      <router-link to="/overview" class="sidebar-link" active-class="active" exact v-if="hasRole(['supusr'])">
        <span class="icon">🏠</span>
        <span v-if="!collapsed">Overview</span>
      </router-link>
      <router-link to="/cms" class="sidebar-link" active-class="active">
        <span class="icon">🏷️</span>
        <span v-if="!collapsed">CMS</span>
      </router-link>
      <router-link to="/features" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">⚙️</span>
        <span v-if="!collapsed">Features</span>
      </router-link>
      <router-link to="/dealerships" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">🏢</span>
        <span v-if="!collapsed">Dealerships</span>
      </router-link>
      <router-link to="/targetsites" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">🌐</span>
        <span v-if="!collapsed">Target Sites</span>
      </router-link>
      <!-- <router-link to="/mapper" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">↔️</span>
        <span v-if="!collapsed">Mapper</span>
      </router-link> -->
      <router-link to="/mappings" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">↔️</span>
        <span v-if="!collapsed">Mappings</span>
      </router-link>
      <router-link to="/automapper" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">🚗</span>
        <span v-if="!collapsed">Automapper</span>
      </router-link>
    </nav>
    <div class="sidebar-bottom">
      <router-link to="/goaltracker" class="sidebar-link" active-class="active" v-if="hasRole(['supusr'])">
        <span class="icon">🎯</span>
        <span v-if="!collapsed">Goal Tracker</span>
      </router-link>
      <button class="theme-switch" @click="toggleTheme" :title="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
        <span v-if="theme === 'dark'">🌙</span>
        <span v-else>☀️</span>
        <span v-if="!collapsed" class="theme-label">{{ theme === 'dark' ? 'Dark' : 'Light' }} Mode</span>
      </button>
      <button class="logout-btn" @click="handleAuthAction" :title="isLoggedIn ? 'Logout' : 'Login'">
        <span>👤</span>
        <span v-if="!collapsed" class="logout-label">{{ isLoggedIn ? 'Log out' : 'Log in' }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
import AdminDark from '@/assets/admin-dark.svg?url'
import AdminLight from '@/assets/admin-light.svg?url'
export default {
  name: 'AppSidebar',
  emits: ['sidebar-toggle'],
  data() {
    return {
      collapsed: false,
      theme: localStorage.getItem('theme') || 'light',
      AdminLight,
      AdminDark,
      currentUser: null
    }
  },
  computed: {
    isLoggedIn() {
      return this.currentUser !== null
    }
  },
  watch: {
    collapsed(newVal) {
      this.$emit('sidebar-toggle', newVal)
    }
  },
  mounted() {
    this.applyTheme(this.theme)
    this.getCurrentUser()
    this.subscribeToUserChanges()
  },
  methods: {
    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark'
      localStorage.setItem('theme', this.theme)
      this.applyTheme(this.theme)
    },
    applyTheme(theme) {
      if (theme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark')
      } else {
        document.documentElement.setAttribute('data-theme', 'light')
      }
    },
    async getCurrentUser() {
      try {
        this.currentUser = await window.seezSdk.getCurrentUser()
      } catch (error) {
        console.error('Error getting current user:', error)
        this.currentUser = null
      }
    },
    subscribeToUserChanges() {
      if (window.seezSdk?.userSubscribe) {
        window.seezSdk.userSubscribe(user => {
          this.currentUser = user
        })
      }
    },
    hasRole(roles) {
      const user = this.currentUser
      if (!user || !user.roles) return false
      return user.roles.some(r => roles.includes(r.rolecode))
    },
    async handleAuthAction() {
      if (this.isLoggedIn) {
        // User is logged in, so logout
        await this.logout()
      } else {
        // User is logged out, so show login
        this.showLogin()
      }
    },
    async logout() {
      if (window.seezSdk && window.seezSdk.logout) {
        await window.seezSdk.logout()
      }
      window.location.replace('/')
    },
    showLogin() {
      if (window.seezSdk && window.seezSdk.showLogin) {
        window.seezSdk.showLogin()
      }
    }
  }
}
</script>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 180px;
  height: 100vh;
  background: var(--color-bg-card);
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  z-index: 100;
  box-shadow: 2px 0 8px rgba(60, 60, 60, 0.04);
  transition: width 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  /* Make sure the sidebar is the containing block for the absolute button */
  overflow: visible;
}
.sidebar.collapsed {
  width: 64px;
}
.sidebar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 1rem 1rem 1rem;
}
.sidebar-logo {
  font-size: 1.3em;
  font-weight: bold;
  color: var(--color-primary);
  letter-spacing: 2px;
  text-align: left;
  transition:
    font-size 0.18s,
    width 0.18s;
  display: flex;
  align-items: center;
  height: 40px;
  width: 180px;
  overflow: hidden;
}
.sidebar-logo-collapsed {
  width: 32px !important;
  transition: width 0.18s;
}
.sidebar-logo-img {
  height: 20px;
  width: auto;
  display: block;
}
.collapse-btn {
  background: none;
  border: none;
  color: var(--color-text-light);
  font-size: 1.2em;
  cursor: pointer;
  padding: 0.2em 0.4em;
  border-radius: 4px;
  transition: background 0.15s;
}
.collapse-btn:hover {
  background: #f1f5f9;
}
.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.2em;
  padding: 0 0.5em;
}
.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.7em;
  padding: 0.55em 0.7em;
  border-radius: 4px;
  color: var(--color-text-light);
  font-size: 1em;
  text-decoration: none;
  transition:
    background 0.2s,
    color 0.2s;
  min-width: 0;
}
.sidebar.collapsed .sidebar-link {
  justify-content: center;
  padding: 0.55em 0.2em;
  gap: 0;
}
.sidebar-link.active,
.sidebar-link:hover {
  background: var(--color-primary);
  color: #fff;
  transition:
    background 0.15s,
    color 0.15s;
}
.icon {
  font-size: 1.25em;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.7em;
  min-width: 1.7em;
  text-align: center;
}
.sidebar.collapsed .sidebar-logo {
  font-size: 1em;
}
.sidebar.collapsed .sidebar-logo {
  text-align: center;
  width: 100%;
}
.sidebar-bottom {
  margin-top: auto;
  padding: 1.2em 0.7em 1em 0.7em;
  display: flex;
  justify-content: center;
  flex-direction: column;
}
.theme-switch {
  display: flex;
  align-items: center;
  gap: 0.5em;
  background: none;
  border: none;
  color: var(--color-text-light);
  font-size: 1em;
  cursor: pointer;
  border-radius: 4px;
  padding: 0.4em 1em;
  transition:
    background 0.15s,
    color 0.15s;
}
.theme-switch:hover {
  background: var(--color-primary);
  color: #fff;
  transition:
    background 0.15s,
    color 0.15s;
}
.theme-label {
  font-size: 1em;
  font-weight: 500;
}
.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.5em;
  background: none;
  border: none;
  color: var(--color-text-light);
  font-size: 1em;
  cursor: pointer;
  border-radius: 4px;
  padding: 0.4em 1em;
  transition:
    background 0.15s,
    color 0.15s;
  margin-top: 0.5em;
}
.logout-btn:hover {
  background: var(--color-primary);
  color: #fff;
}
.logout-label {
  font-size: 1em;
}
.collapse-btn-vertical {
  position: absolute;
  right: -14px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 48px;
  background: var(--color-bg-card);
  border: none;
  border-radius: var(--border-radius);
  color: var(--color-text-light);
  font-size: 1.2em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 0 8px rgba(60, 60, 60, 0.04);
  z-index: 101;
  transition:
    right 0.18s,
    background 0.18s;
}
.collapse-btn-vertical:hover {
  background: var(--color-primary);
  color: #fff;
}
</style>
